import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  FloppyDisk, ArrowCounterClockwise, ClockCounterClockwise, Plus, Trash, BookOpenText, ArrowClockwise, GitBranch,
} from "@phosphor-icons/react";
import { iconFor } from "@/components/playbook/prompts";

const TYPE_LABEL = {
  trusted_advisor: "Trusted Advisor",
  strategic_initiative: "Strategic Initiative",
  executive_convening: "Executive Convening",
  executive_communication: "Executive Comms",
};

/**
 * Authoring UI for the four Yadid engagement playbooks.
 * Left rail: pick a playbook. Right pane: edit label, blurb, sections, prompts.
 * Every save creates a new version; older versions can be reactivated inline.
 */
export default function PlaybooksAuthoring() {
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [current, setCurrent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/playbooks");
    setPlaybooks(data);
    if (!selectedType && data.length) setSelectedType(data[0].engagement_type);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedType) return;
    const found = playbooks.find((p) => p.engagement_type === selectedType);
    if (found) {
      setCurrent(JSON.parse(JSON.stringify(found)));
      setDirty(false);
    }
  }, [selectedType, playbooks]);

  const patch = (fn) => {
    setCurrent((c) => {
      const next = JSON.parse(JSON.stringify(c));
      fn(next);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await api.put(`/playbooks/${current.engagement_type}`, {
        label: current.label,
        blurb: current.blurb,
        note: `Edited via Settings on ${new Date().toISOString().slice(0,10)}`,
        sections: current.sections.map((s) => ({
          key: s.key, label: s.label, icon_name: s.icon_name || "Circle",
          blurb: s.blurb, prompts: s.prompts.filter((p) => p.trim()),
        })),
      });
      toast.success("Playbook saved as a new version");
      await load();
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!current) return;
    if (!window.confirm(`Reset "${TYPE_LABEL[current.engagement_type]}" to canonical? Every override on this playbook will be deleted permanently.`)) return;
    await api.post(`/playbooks/${current.engagement_type}/reset`);
    toast.success("Reverted to canonical playbook");
    await load();
    setDirty(false);
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    const { data } = await api.get(`/playbooks/${current.engagement_type}/versions`);
    setVersions(data.versions || []);
  };

  const activateVersion = async (v) => {
    await api.post(`/playbooks/${current.engagement_type}/versions/${v}/activate`);
    toast.success(`Version ${v} is now active`);
    setHistoryOpen(false);
    await load();
  };

  if (!current) {
    return (
      <div className="border border-border bg-card p-6 md:col-span-2" data-testid="playbooks-authoring">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5">
          <BookOpenText size={12} weight="fill" /> Playbook authoring
        </div>
        <div className="text-xs text-muted-foreground mt-3">Loading playbooks…</div>
      </div>
    );
  }

  return (
    <div className="border border-border bg-card md:col-span-2" data-testid="playbooks-authoring">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary flex items-center gap-1.5">
            <BookOpenText size={12} weight="fill" /> Playbook authoring
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Fork the canonical playbooks · versioned · reversible
          </div>
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          {current.is_canonical ? "canonical" : `v${current.version}`}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] divide-x divide-border">
        {/* Playbook picker */}
        <div className="p-4 space-y-1">
          {playbooks.map((p) => (
            <button
              key={p.engagement_type}
              onClick={() => setSelectedType(p.engagement_type)}
              data-testid={`playbook-pick-${p.engagement_type}`}
              className={`w-full text-left px-3 py-2 text-xs uppercase font-mono tracking-widest border-l-2 transition ${
                selectedType === p.engagement_type
                  ? "border-primary text-primary bg-primary/5"
                  : "border-transparent text-muted-foreground hover:text-primary hover:border-primary/40"
              }`}
            >
              <div>{TYPE_LABEL[p.engagement_type] || p.engagement_type}</div>
              <div className="text-[0.5625rem] normal-case tracking-normal text-muted-foreground/70 mt-0.5">
                {p.is_canonical ? "canonical" : `v${p.version} · custom`}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Playbook title</label>
              <Input
                data-testid="playbook-editor-label"
                value={current.label}
                onChange={(e) => patch((c) => { c.label = e.target.value; })}
                className="rounded-none bg-secondary/40 mt-1"
              />
            </div>
            <div>
              <label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Blurb</label>
              <Input
                value={current.blurb}
                onChange={(e) => patch((c) => { c.blurb = e.target.value; })}
                className="rounded-none bg-secondary/40 mt-1"
              />
            </div>
          </div>

          {current.sections.map((s, sIdx) => {
            const Icon = iconFor(s.icon_name);
            return (
              <div key={s.key} className="border border-border/60 p-4" data-testid={`playbook-editor-section-${s.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={14} weight="bold" className="text-primary" />
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">{s.key}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <Input
                    value={s.label}
                    onChange={(e) => patch((c) => { c.sections[sIdx].label = e.target.value; })}
                    placeholder="Section label"
                    className="rounded-none bg-secondary/40"
                  />
                  <Input
                    value={s.blurb}
                    onChange={(e) => patch((c) => { c.sections[sIdx].blurb = e.target.value; })}
                    placeholder="Section blurb"
                    className="rounded-none bg-secondary/40"
                  />
                </div>
                <div className="space-y-2">
                  {s.prompts.map((p, pIdx) => (
                    <div key={pIdx} className="flex items-start gap-2">
                      <div className="text-[0.625rem] font-mono text-muted-foreground pt-2 shrink-0 w-6 text-right">{pIdx + 1}.</div>
                      <Textarea
                        rows={2}
                        value={p}
                        onChange={(e) => patch((c) => { c.sections[sIdx].prompts[pIdx] = e.target.value; })}
                        data-testid={`playbook-editor-prompt-${s.key}-${pIdx}`}
                        className="rounded-none bg-secondary/40 text-xs"
                      />
                      <button
                        onClick={() => patch((c) => { c.sections[sIdx].prompts.splice(pIdx, 1); })}
                        className="text-muted-foreground hover:text-destructive p-1"
                        title="Remove prompt"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => patch((c) => { c.sections[sIdx].prompts.push(""); })}
                  data-testid={`playbook-editor-add-prompt-${s.key}`}
                  className="mt-2 text-[0.625rem] uppercase font-mono tracking-widest text-primary hover:opacity-80 inline-flex items-center gap-1"
                >
                  <Plus size={12} /> Add prompt
                </button>
              </div>
            );
          })}

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
            <Button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="playbook-editor-save"
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <FloppyDisk size={14} weight="bold" className="mr-2" />
              {saving ? "Saving…" : dirty ? "Save as new version" : "Saved"}
            </Button>
            <Button
              onClick={openHistory}
              variant="outline"
              data-testid="playbook-editor-history"
              className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
            >
              <ClockCounterClockwise size={14} weight="bold" className="mr-2" /> Version history
            </Button>
            <Button
              onClick={reset}
              variant="outline"
              disabled={current.is_canonical}
              data-testid="playbook-editor-reset"
              className="rounded-none border-border text-muted-foreground hover:text-destructive hover:border-destructive/60"
            >
              <ArrowCounterClockwise size={14} weight="bold" className="mr-2" /> Reset to canonical
            </Button>
            {dirty && (
              <span className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">
                Unsaved changes
              </span>
            )}
          </div>
        </div>
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-none bg-card" data-testid="playbook-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <GitBranch size={16} weight="fill" className="text-primary" />
              {TYPE_LABEL[current.engagement_type]} · version history
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {versions.length === 0 && (
              <div className="text-sm text-muted-foreground italic">No custom versions yet — you're on canonical.</div>
            )}
            {versions.map((v) => (
              <div
                key={v.version}
                className={`border p-3 flex items-start justify-between gap-3 ${v.is_active ? "border-primary bg-primary/5" : "border-border"}`}
                data-testid={`playbook-version-row-${v.version}`}
              >
                <div className="min-w-0">
                  <div className="text-xs font-medium">Version {v.version} {v.is_active && <span className="text-[0.625rem] font-mono uppercase tracking-widest text-primary ml-2">active</span>}</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                    {v.created_at?.slice(0, 10)} · by {v.created_by_name || v.created_by_email || "—"}
                  </div>
                  {v.note && <div className="text-xs text-muted-foreground mt-1">{v.note}</div>}
                </div>
                {!v.is_active && (
                  <button
                    onClick={() => activateVersion(v.version)}
                    data-testid={`playbook-activate-${v.version}`}
                    className="text-[0.625rem] uppercase font-mono tracking-widest border border-primary/60 text-primary px-3 py-1.5 hover:bg-primary/10 shrink-0"
                  >
                    <ArrowClockwise size={11} className="inline mr-1" /> Activate
                  </button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setHistoryOpen(false)} className="rounded-none bg-secondary text-secondary-foreground">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
